import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, tap, throwError, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { ApiError } from '../model/api-error.model';
import AppError from '../model/app-error';
import { AppType } from '../model/app-type.model';
import { AuthTokenDecoded, AuthTokenResponse } from '../model/auth-token';
import { ApiAuthEndpoints, ApiService } from '../services/api.service';
import { PersistStorageKeys, PersistStorageService } from '../services/persist-storage.service';
import { ProcessService } from '../services/process.service';
import { isEmpty } from '../utils';
import decodeToken from '../utils/decodeToken';

enum HttpStatus {
  continue = 100,
  ok = 200,
  created = 201,
  accepted = 202,
  no_content = 204,
  bad_request = 400,
  unauthorized = 401,
  forbidden = 403,
  not_found = 404,
  internal_server_error = 500,
  service_unavailable = 503,
}

// Configuration for the AuthInterceptor
const UNSECURED_ENDPOINTS_INCLUDES = [
  ApiAuthEndpoints.Auth,
  ApiAuthEndpoints.AuthLogged,
  ApiAuthEndpoints.GlobtroterMenu,
  ApiAuthEndpoints.GlobtroterUser,
  ApiAuthEndpoints.GlobtroterConfig,
];
const UNSECURED_ENDPOINTS_STARTS_WITH = [`${environment.deployUrl}assets/`];
const RETRY_COUNT_INIT = 0;
const RETRIES_LIMIT = 3;
const RETRIES_DELAY = 500;
const RETRY_STATUS_CODES = [HttpStatus.unauthorized, HttpStatus.forbidden];

/**
 * AuthInterceptor
 * This code defines an Angular HTTP interceptor called AuthInterceptor.
 * The interceptor is responsible for adding authentication tokens to secured
 * API requests and handling errors and redirects related to authentication.
 */

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  retryCount = RETRY_COUNT_INIT;

  constructor(private apiService: ApiService, private persistStorage: PersistStorageService, private processService: ProcessService) {}

  /**
   * Intercepts HTTP requests and adds authentication tokens if required.
   * It also handles errors and redirects related to authentication.
   *
   * @param request The HTTP request.
   * @param next The HTTP handler for the next interceptor in the chain.
   * @returns An Observable of the HTTP event.
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isSecuredEndpoint(request.url)) {
      let sessionToken = this.persistStorage.getString(PersistStorageKeys.SessionToken);

      if (!sessionToken) {
        return this.refreshSessionToken().pipe(
          switchMap(() => {
            sessionToken = this.persistStorage.getString(PersistStorageKeys.SessionToken);

            if (!sessionToken) {
              return throwError(() => new AppError('auth', null, 'UNAUTHORIZED'));
            }

            request = this.addToken(request, sessionToken);

            return this.handleRequest(next, request);
          }),
        );
      }

      request = this.addToken(request, sessionToken);
    }

    return this.handleRequest(next, request);
  }

  /**
   * Checks if the provided URL is a secured endpoint that requires authentication.
   *
   * @param url The URL to check.
   * @returns true if the URL is a secured endpoint, false otherwise.
   */
  private isSecuredEndpoint(url: string): boolean {
    if (environment.app === AppType.Direct) {
      return false;
    }

    return !(
      UNSECURED_ENDPOINTS_INCLUDES.some((endpoint) => url.includes(endpoint)) ||
      UNSECURED_ENDPOINTS_STARTS_WITH.some((endpoint) => url.startsWith(endpoint))
    );
  }

  /**
   * Adds the authentication token to the request headers and returns
   * a new cloned request object with the token added.
   *
   * @param request The HTTP request.
   * @param token The authentication token.
   * @returns A new HttpRequest object with the token added to the headers.
   */
  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    const headers = request.headers.set('Authorization', `Bearer ${token}`);

    return request.clone({
      headers,
    });
  }

  /**
   * Refreshes the session token by making a request to the apiService to get a new authentication token.
   * It updates the session token in the persistStorage service. If the refresh token request fails,
   * it throws an AppError with the error details.
   *
   * @returns An Observable of the authentication token response.
   */
  private refreshSessionToken() {
    this.persistStorage.removeItem(PersistStorageKeys.SessionToken, 'session');

    return this.apiService.getAuthToken().pipe(
      tap((response: AuthTokenResponse) => {
        const newToken = response.access_token;
        const decodedToken = decodeToken(newToken) as AuthTokenDecoded;

        this.persistStorage.setString(PersistStorageKeys.SessionToken, newToken, 'session');
        this.persistStorage.setString(PersistStorageKeys.AgentCategory, String(decodedToken.agent_category), 'session');
        this.persistStorage.setString(PersistStorageKeys.GivenName, String(decodedToken.given_name), 'session');
        this.persistStorage.setString(PersistStorageKeys.FamilyName, String(decodedToken.family_name), 'session');
        this.persistStorage.setString(PersistStorageKeys.AgentMail, String(decodedToken.agent_email), 'session');
        if (decodedToken.agent_phone) {
          this.persistStorage.setString(PersistStorageKeys.AgentPhone, String(decodedToken.agent_phone), 'session');
        }
        const storedAgentNumber = String(decodedToken.az_agent_number) === 'undefined' ? undefined : String(decodedToken.az_agent_number);
        if (storedAgentNumber) {
          this.persistStorage.setString(PersistStorageKeys.AgentNumber, storedAgentNumber, 'session');
        }
        this.processService.agentNumberFromToken$.next(
          String(decodedToken.az_agent_number) === 'undefined' ? undefined : String(decodedToken.az_agent_number),
        );
      }),
      catchError((error: AppError) => throwError(() => new AppError('auth', error?.code || null, 'REFRESH_TOKEN_FAILED'))),
    );
  }

  /**
   * Handles the actual HTTP request by passing it to the next handler.
   * It also implements retry logic for certain error status codes and handles error responses by
   * throwing appropriate AppError instances.
   *
   * @param next The HTTP handler for the next interceptor in the chain.
   * @param request The HTTP request.
   * @returns An Observable of the HTTP event.
   */
  private handleRequest(next: HttpHandler, request: HttpRequest<any>): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError(({ status, error }: HttpErrorResponse) => {
        // Retry logic for certain error status codes
        if (this.retryCount < RETRIES_LIMIT && RETRY_STATUS_CODES.includes(status)) {
          this.retryCount++;

          return timer(RETRIES_DELAY).pipe(
            concatMap(() =>
              this.refreshSessionToken().pipe(
                switchMap(() => {
                  const sessionToken = this.persistStorage.getString(PersistStorageKeys.SessionToken);

                  if (!sessionToken) {
                    return throwError(() => new AppError('auth', null, 'UNAUTHORIZED'));
                  }

                  request = this.addToken(request, sessionToken);

                  return this.handleRequest(next, request);
                }),
              ),
            ),
          );
        }

        // Handle error responses
        this.retryCount = RETRY_COUNT_INIT;

        switch (status) {
          case HttpStatus.unauthorized:
            this.authRedirect(request.url, status);
            this.persistStorage.removeItem(PersistStorageKeys.SessionToken, 'session');
            throw new AppError('auth', status, 'UNAUTHORIZED');
          case HttpStatus.forbidden:
            throw new AppError('auth', status, 'FORBIDDEN');
          default:
            if (this.isApiError(error)) {
              const apiErrors = (error as ApiError).errors;
              if (Array.isArray(apiErrors)) {
                const errors =
                  apiErrors?.filter(({ code, userMessage }) => code !== userMessage).map(({ userMessage }) => userMessage) || [];

                const errorCodes = apiErrors.map(({ code }) => code) || [];

                throw new AppError('app', status, errors.join(', '), errorCodes);
              } else if (typeof apiErrors === 'object') {
                const errors =
                  Object.entries(apiErrors)
                    .filter(([code, userMessages]) => code !== userMessages.join(' '))
                    .map(([_, userMessages]) => userMessages)
                    .flat() || [];

                const errorCodes = Object.keys(apiErrors);

                throw new AppError('app', status, errors.join(', '), errorCodes);
              }
            }

            throw new AppError('app', status, '');
        }
      }),
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.retryCount = RETRY_COUNT_INIT;
        }
      }),
    );
  }

  /**
   * Handles redirects related to authentication. If the URL and status indicate an authentication failure,
   * it performs a redirect to the SSO (Single Sign-On) page if the application is in production and the SSO URL is configured.
   *
   * @param url The URL of the request.
   * @param status The HTTP status code of the response.
   */
  private authRedirect(url: string, status: number) {
    const isAllowed =
      (url.includes(ApiAuthEndpoints.AuthLogged) && status >= HttpStatus.bad_request) ||
      (url.includes(ApiAuthEndpoints.Auth) && status >= HttpStatus.bad_request) ||
      status === HttpStatus.unauthorized ||
      status === HttpStatus.forbidden;

    if (isAllowed && environment.sso && environment.production) {
      location.replace(environment.sso);
    }
  }

  /**
   * Checks if the provided error object is an API error. It verifies the structure of the error object
   * and its errors property to determine if it is an API error.
   *
   * @param error The error object to check.
   * @returns true if the error object is an API error, false otherwise.
   */
  private isApiError(error: any): boolean {
    if (typeof error !== 'object') {
      return false;
    }

    if (!('errors' in error)) {
      return false;
    }

    const errors = (error as { errors: any[] | { [key in string]: any[] } }).errors;

    if ((!Array.isArray(errors) || isEmpty(errors)) && typeof errors !== 'object') {
      return false;
    }

    if (Array.isArray(errors)) {
      return errors.every(({ code, userMessage }) => typeof code === 'string' && typeof userMessage === 'string');
    } else {
      return Object.values(errors).every((userMessages) => userMessages.every((userMessage) => typeof userMessage === 'string'));
    }
  }
}
